import React, { useState } from "react";
import { useCurrency } from "../context/CurrencyContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PasswordInput from "../components/PasswordInput";
import SessionContractCard from "../components/SessionContractCard";
import api, { setStoredAccessToken, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Save, IndianRupee, User, ShieldCheck, Loader2 } from "lucide-react";

export default function Settings() {
  const { rate, updateRate, currency } = useCurrency();
  const { user } = useAuth();
  const [val, setVal] = useState(String(rate));
  const [saving, setSaving] = useState(false);

  // Change-password form state (iter132)
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  const canChangePw = currentPw.length >= 8 && newPw.length >= 8 && confirmPw.length >= 8
                      && newPw === confirmPw && newPw !== currentPw && !changingPw;

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (!canChangePw) return;
    setChangingPw(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
        confirm_password: confirmPw,
      });
      // Keep THIS session alive with the fresh token backend returned.
      if (data?.access_token) setStoredAccessToken(data.access_token);
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
      toast.success("Password changed. Other sessions have been signed out.");
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't change password";
      toast.error(msg);
    } finally {
      setChangingPw(false);
    }
  };

  const onSave = async (e) => {
    e.preventDefault();
    const n = parseFloat(val);
    if (Number.isNaN(n) || n <= 0) {
      toast.error("Rate must be a positive number");
      return;
    }
    setSaving(true);
    try {
      await updateRate(n);
      toast.success(`Conversion rate updated to ${n}`);
    } catch (err) {
      toast.error("Failed to update rate");
    } finally {
      setSaving(false);
    }
  };

  React.useEffect(() => { setVal(String(rate)); }, [rate]);

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div>
        <p className="bt-label">Preferences</p>
        <h1 className="font-heading text-4xl">Settings</h1>
        <p className="text-sm text-[var(--bt-text-muted)] mt-1">
          Tune your journal to your trading reality.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Session Contract — iter133 Phase 4A (free-tier AI Coach surface) */}
        <SessionContractCard />

        {/* Profile card */}
        <div className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-sm border border-[var(--bt-border)] flex items-center justify-center">
              <User className="w-4 h-4 text-[var(--bt-accent)]" />
            </span>
            <div>
              <p className="bt-label">Profile</p>
              <h2 className="font-heading text-xl">{user?.name || "—"}</h2>
            </div>
          </div>
          <div className="space-y-2 pt-2 border-t border-[var(--bt-border)]">
            <div className="flex justify-between text-sm">
              <span className="text-[var(--bt-text-muted)]">Email</span>
              <span className="font-mono">{user?.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[var(--bt-text-muted)]">Role</span>
              <span className="font-mono uppercase">{user?.role}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-[var(--bt-text-muted)]">Display Currency</span>
              <span className="text-[var(--bt-accent)] font-bold">{currency}</span>
            </div>
          </div>
        </div>

        {/* Rate card */}
        <form onSubmit={onSave} className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-sm border border-[var(--bt-border)] flex items-center justify-center">
              <IndianRupee className="w-4 h-4 text-[var(--bt-accent)]" />
            </span>
            <div>
              <p className="bt-label">Currency Conversion</p>
              <h2 className="font-heading text-xl">USD → INR Rate</h2>
            </div>
          </div>
          <p className="text-sm text-[var(--bt-text-muted)]">
            Used to convert P&L between currencies when you toggle between $ USD and ₹ INR. Default is 85.
          </p>
          <div className="space-y-2">
            <Label className="bt-label">1 USD =</Label>
            <Input
              type="number" step="0.01" min="0"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              data-testid="conversion-rate-input"
              className="bg-transparent border-[var(--bt-border)] focus-visible:border-[var(--bt-accent)] focus-visible:ring-1 focus-visible:ring-[var(--bt-accent)] h-11 rounded-sm bt-num"
            />
          </div>
          <Button
            type="submit" disabled={saving}
            data-testid="save-rate-btn"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-11 w-full">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? "Saving…" : "Save rate"}
          </Button>
        </form>

        {/* Security — Change Password (iter132) */}
        <form
          onSubmit={onChangePassword}
          className="bg-[var(--bt-surface)] border border-[var(--bt-border)] rounded-sm p-6 space-y-4 lg:col-span-2"
          data-testid="change-password-form"
        >
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-sm border border-[var(--bt-border)] flex items-center justify-center">
              <ShieldCheck className="w-4 h-4 text-[var(--bt-accent)]" />
            </span>
            <div>
              <p className="bt-label">Security</p>
              <h2 className="font-heading text-xl">Change Password</h2>
            </div>
          </div>
          <p className="text-sm text-[var(--bt-text-muted)]">
            Rotate your BrainTRADES password. Changing it will sign you out of every other browser and device you're logged in on — this session stays active.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="bt-label">Current password</Label>
              <PasswordInput
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder="Enter current password"
                testId="change-password-current"
                autoComplete="current-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="bt-label">New password</Label>
              <PasswordInput
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder="At least 8 characters"
                testId="change-password-new"
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label className="bt-label">Confirm new password</Label>
              <PasswordInput
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                placeholder="Retype new password"
                testId="change-password-confirm"
                autoComplete="new-password"
              />
              {confirmPw.length > 0 && newPw !== confirmPw && (
                <p className="text-xs" style={{ color: "var(--bt-loss)" }} data-testid="change-password-mismatch">
                  Passwords don't match
                </p>
              )}
            </div>
          </div>

          <Button
            type="submit"
            disabled={!canChangePw}
            data-testid="change-password-submit"
            className="bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold rounded-sm h-11"
          >
            {changingPw ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Changing…</>
            ) : (
              <><ShieldCheck className="w-4 h-4 mr-1.5" /> Change password</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
